import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {
    AssetInspectionResultDto,
    FieldType,
    FormField,
    FormSection,
    FormViewModeType,
    InspectionResultFullDto,
} from '@core/interfaces/engin/maintenance-planning/form-visualization';
import {FormService} from '@theme/components/card-components/inspection-form/form.service';
import {BehaviorSubject, timer} from 'rxjs';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {FormCanvasComponent} from '@theme/components/card-components/inspection-form/form-canvas/form-canvas.component';
import {FormMode} from '@core/interfaces/engin/maintenance-planning/maintenance-planning';
import {S3Service} from '@core/interfaces/common/s3';
import {distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {MediaDownloadService} from '@core/interfaces/common/media-download';
import {NbToastrService} from '@nebular/theme';
import {DomSanitizer} from '@angular/platform-browser';
import {InspectionStatsDto} from '@core/interfaces/engin/maintenance-planning/deficiencies.model';
import {ButtonActionType, ResultFieldTypes, TableValidation, TrackProgress} from '@core/interfaces/common/form';

@Component({
    selector: 'ngx-form-parent',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [FormService],
})
export class FormComponent extends Unsubscribable implements OnInit, AfterViewInit, OnChanges {
    @ViewChild(FormCanvasComponent) formCanvasComponent: FormCanvasComponent;
    private multipleSelectionType = [
        FieldType.MULTI_SELECT,
        FieldType.IMAGE_VIDEO,
        FieldType.IMAGE,
        FieldType.VIDEO,
        FieldType.ISSUES,
        // TODO: these are not field types? Need to check internals.
        FieldType.TABLE_NUMBER_COLUMNS,
        FieldType.TABLE_TEXT_COLUMNS,
    ];
    private supportTypes = [
        FieldType.NUMERIC,
        FieldType.TEXT,
        FieldType.CHECKBOX,
        FieldType.SINGLE_SELECT,
        FieldType.MULTI_SELECT,
        FieldType.IMAGE,
        FieldType.VIDEO,
        FieldType.IMAGE_VIDEO,
        FieldType.SIGNATURE,
        FieldType.ISSUES,
        FieldType.TABLE,
    ];
    @Input() dataSource: InspectionResultFullDto;
    @Input() viewMode: FormViewModeType;
    @Input() pageMode: BehaviorSubject<FormMode> = new BehaviorSubject<FormMode>(null);
    @Input() s3service: S3Service;
    @Input() totalDeficiencies: InspectionStatsDto;
    @Input() selectedPath: {sectionId: number; subsectionId: number};
    @Input() selectedFieldId?: number;
    @Output() formValueChangeEvent = new EventEmitter();
    @Output() completedRequiredFields = new EventEmitter();
    @Output() scrollToTop = new EventEmitter();
    @Output() onAddDeficiency = new EventEmitter<{fieldType: FieldType; fieldId: number}>();
    completedRequiredFieldsProgress = new BehaviorSubject<number>(0);
    continueEvent: number = 0;
    FormViewModeType = FormViewModeType;
    sectionRequiredStatus: FormGroup = this.fb.group({
        total: 0,
        completed: 0,
        invalid: 0,
    });
    totalRequiredStatus: FormGroup = this.fb.group({
        total: 0,
        completed: 0,
        invalid: 0,
    });
    activeSection: FormSection;
    formSections: FormSection[] = [];
    allFieldList: FormField[] = [];
    currentFormResults: AssetInspectionResultDto;
    previousFormResults: AssetInspectionResultDto;
    currentDisplayFields: AbstractControl[] = [];
    currentDisplayOptionalFields: AbstractControl[] = [];
    currentDisplayTableFields: TableValidation;
    fieldResultForm: FormGroup = this.fb.group({});
    formIsReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    lastSubSection: boolean = false;
    firstSection: boolean = false;
    nextBtnDisabled: boolean = false;
    previousBtnDisabled: boolean = false;
    totalInvalidFieldsList: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);
    buttonAction: ButtonActionType = ButtonActionType.Next;
    assetFormId: number = null;

    constructor(
        protected cd: ChangeDetectorRef,
        private fb: FormBuilder,
        private formService: FormService,
        private mediaDownloadService: MediaDownloadService,
        private toastrService: NbToastrService,
        private sanitizer: DomSanitizer,
    ) {
        super();
    }

    ngOnInit(): void {
        // Set viewMode, based on pageMode, if not already defined
        if (this.viewMode == null) {
            this.viewMode =
                this.pageMode.getValue() == FormMode.EDIT
                    ? FormViewModeType.EDIT_RESULTS
                    : FormViewModeType.VIEW_RESULTS;
        }

        this.initializeForm();
        // Run init process each time the pagemode changes
        this.pageMode.subscribe((pageMode) => {
            if (this.viewMode !== FormViewModeType.VIEW_CONFIG) {
                this.viewMode =
                    pageMode == FormMode.EDIT ? FormViewModeType.EDIT_RESULTS : FormViewModeType.VIEW_RESULTS;
            }
            this.initializeForm();
            // On first load this is handled with ngAfterViewInit
            this.updateTotalRequiredValues();
            this.updateSectionRequiredValues();
        });

        this.totalRequiredStatus.valueChanges.pipe(distinctUntilChanged()).subscribe((value: TrackProgress) => {
            const progressBarValue: number = value.total === 0 ? 100 : (value.completed / value.total) * 100;
            this.completedRequiredFieldsProgress.next(Math.round(progressBarValue));
            this.totalRequiredStatusEmitEvent();
        });
        if (this.dataSource?.currentAssetInspectionResult?.mediaFields) this.loadMediaPreviews();
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.selectedPath && changes.selectedPath.currentValue !== changes.selectedPath.previousValue) {
            if (!this.selectedPath.sectionId) {
                const section = this.findSectionById(this.formSections, this.selectedPath.subsectionId);

                if (!section) return;
                this.selectedPath = {
                    sectionId: section.id,
                    subsectionId: this.selectedPath.subsectionId,
                };
            }
        }
    }

    private initializeForm(): void {
        // Reset fields before initializing, since we may call this method multiple times after for page mode changes
        this.formSections = [];
        this.fieldResultForm = this.fb.group({});

        this.currentFormResults = this.dataSource?.currentAssetInspectionResult;
        if (this.dataSource?.inspectionResultCode) {
            this.currentFormResults.inspectionResultCode = this.dataSource?.inspectionResultCode;
        }
        this.previousFormResults = this.dataSource?.previousAssetInspectionResult;
        this.assetFormId = this.dataSource?.assetFormId;
        this.dataSource?.formResponse.sections.forEach((section: FormSection) => {
            this.formSections.push(section);
            // Initialize sections only when editing results, since this will trigger validations
            if (this.viewMode == FormViewModeType.EDIT_RESULTS) {
                this.initSection(section);
            }
            this.formIsReady$.next(true);
        });
    }

    // Fetch media as soon as the form opens: presigned S3 URLs expire, a local blob URL does not.
    private loadMediaPreviews() {
        let previewErrorShown = false;
        this.currentFormResults.mediaFields.forEach((mediaField) => {
            if (mediaField.url && !mediaField.isStreaming) {
                this.mediaDownloadService
                    .fetchBlob(mediaField.url.toString())
                    .pipe(takeUntil(this.unsubscribe$))
                    .subscribe(
                        (blob: Blob) => {
                            const blobUrl = URL.createObjectURL(blob);
                            mediaField.url = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
                            mediaField.isStreaming = true;
                        },
                        () => {
                            // One toast per form open: a failure usually means every link in the batch expired.
                            if (previewErrorShown) {
                                return;
                            }
                            previewErrorShown = true;
                            this.toastrService.danger('Reopen the form to try again.', 'Media preview unavailable', {
                                duration: 10000,
                            });
                        },
                    );
            }
        });
    }

    // Initialize form control for all form fields, for a particular section (recursive)
    private initSection(s: FormSection): void {
        if (s.sections?.length > 0) {
            s.sections.forEach((sub) => this.initSection(sub));
        }
        if (s.fields?.length > 0) {
            this.initFields(s.fields);
        }
    }

    // Initialize form control for all form fields
    private initFields(fields: FormField[]): void {
        fields.forEach((f) => {
            this.allFieldList.push(f);
            if (this.supportTypes.includes(f.fieldType)) {
                /*
                 * checkRequiredAndViable tracks all required fields throughout the form, if a field cannot be
                 * completed (i.e., select-type field but has no configured options).
                 */

                {
                    const result = this.formService.getResultsByField(f, this.currentFormResults);
                    const value = result ? {value: result} : null;
                    const isRequired =
                        this.formService.checkRequiredAndViable(f, this.currentFormResults) &&
                        this.formService.visibleCheck(f, this.currentFormResults);

                    this.fieldResultForm.addControl(
                        f.id + '',
                        this.fb.control(
                            value, // set the form control getting it from the form service?
                            isRequired ? Validators.required : null,
                        ),
                    );
                }
            }
        });
    }

    public selectionChanged(activeSection: {sectionId: number; subsectionId: number}): void {
        const selectedSection = this.dataSource?.formResponse.sections.find(
            (section: FormSection) => section.id === activeSection.sectionId,
        );

        // Set subsection as active if applicable, otherwise section
        if (activeSection.subsectionId && activeSection.subsectionId != -1) {
            this.activeSection = this.findSectionById(selectedSection.sections, activeSection.subsectionId);
        } else {
            this.activeSection = selectedSection;
        }
        this.checkFormPosition();
        this.getCurrentDisplayFields();
        this.updateSectionRequiredValues();
        this.nextBtnDisabled = this.lastSubSection;
        this.previousBtnDisabled = this.firstSection;
        this.cd.detectChanges();
    }

    private checkFormPosition(): void {
        const section = this.dataSource?.formResponse.sections;
        const firstObject = section[0];
        const firstSubSection = firstObject.sections[0];
        const lastObject = section[section.length - 1];
        const lastSubSection = lastObject.sections[lastObject.sections.length - 1];
        this.lastSubSection =
            JSON.stringify(this.activeSection) === JSON.stringify(lastObject) ||
            JSON.stringify(this.activeSection) === JSON.stringify(lastSubSection);
        this.firstSection =
            JSON.stringify(this.activeSection) === JSON.stringify(firstObject) ||
            JSON.stringify(this.activeSection) === JSON.stringify(firstSubSection);
    }

    /* Reset tracking for all displayed fields (within the active section) */
    private getCurrentDisplayFields(): void {
        const fieldIdArray = [];
        this.currentDisplayFields = [];
        this.currentDisplayOptionalFields = [];

        // Add tracking for fields within the immediate section
        if (this.activeSection?.fields?.length > 0) {
            this.activeSection.fields.forEach((field) => {
                if (this.formService.checkRequiredAndViable(field, this.currentFormResults)) {
                    fieldIdArray.push(field.id.toString());
                }
            });
        }

        // Add tracking for fields within all first-level subsections from the immediate section
        if (this.activeSection?.sections?.length > 0) {
            this.activeSection.sections.forEach((section) => {
                section.fields.map((field) => {
                    if (this.formService.checkRequiredAndViable(field, this.currentFormResults)) {
                        fieldIdArray.push(field.id.toString());
                    }
                });
            });
        }
        this.currentDisplayTableFields = this.getTableValidation(fieldIdArray);
        Object.keys(this.fieldResultForm.controls).forEach((key) => {
            const control = this.fieldResultForm.get(key);
            if (fieldIdArray.includes(key)) {
                this.currentDisplayOptionalFields.push(control);
                if (control.validator) {
                    this.currentDisplayFields.push(control);
                }
            }
        });
    }

    public formValueChange(updateValue): void {
        this.currentFormResults = this.updateOrAddField(updateValue);
        this.updateSectionRequiredValues();
        const validation: boolean = this.currentDisplayFields.every((control) => control.status === 'VALID');
        this.resultEmitEvent();
        const currentDisplayTableFields = this.currentDisplayTableFields.totalInvalidFields
            ? this.currentDisplayTableFields.totalInvalidFields.length
            : 0;
        if (validation && this.lastSubSection && currentDisplayTableFields === 0) {
            this.formCanvasComponent.checkValidationEvent(false);
        }
        this.updateTotalRequiredValues();
        this.cd.detectChanges();
    }

    private updateOrAddField(newFields): AssetInspectionResultDto {
        const selectedField = ResultFieldTypes[newFields.fieldType];
        const updateField = {
            ...newFields,
            value: this.ensureArray(newFields.value),
        };
        const index = this.currentFormResults[selectedField].findIndex((item) => item.fieldId === newFields.fieldId);
        const multipleSelector = this.multipleSelectionType.includes(newFields.fieldType);
        if (index !== -1) {
            if (multipleSelector) {
                // Remove all values with matched fieldId in the original array and add new values
                this.currentFormResults[selectedField] = this.currentFormResults[selectedField].filter(
                    (item) => item?.fieldId !== newFields.fieldId,
                );
                updateField.value.forEach((item) => {
                    this.currentFormResults[selectedField].push(this.resultDto(item, updateField));
                });
            } else {
                if (this.checkEmptyValue(updateField)) {
                    // remove the field if value is empty
                    this.currentFormResults[selectedField] = this.currentFormResults[selectedField].filter(
                        (item) => item.fieldId !== updateField.fieldId,
                    );
                } else {
                    // Update existing field
                    updateField.value.forEach((item) => {
                        this.currentFormResults[selectedField][index] = this.resultDto(item, updateField);
                    });
                }
            }
        } else {
            // Add new field
            updateField.value.forEach((item) => {
                this.currentFormResults[selectedField].push(this.resultDto(item, updateField));
            });
        }
        const originData = this.currentFormResults;
        const updatedData = {
            formId: originData.formId,
            assetId: originData.assetId,
            completedOn: originData.completedOn,
            completedBy: originData.completedBy,
            completedByUserId: originData.completedByUserId,
            ...this.currentFormResults,
        };
        return updatedData;
    }
    private checkEmptyValue(updateField) {
        switch (updateField.fieldType) {
            case FieldType.TEXT:
            case FieldType.NUMERIC:
                return updateField.value[0]?.value === '';
            case FieldType.SINGLE_SELECT:
            case FieldType.MULTI_SELECT:
            case FieldType.SIGNATURE:
                return updateField.value[0] === null;
            default:
                return false;
        }
    }
    private resultDto(item, newFields) {
        switch (newFields.fieldType) {
            case FieldType.TEXT:
            case FieldType.CHECKBOX:
                return {
                    fieldId: item.fieldId,
                    value: item.value,
                    fieldCode: item.fieldCode,
                };
            case FieldType.NUMERIC:
                let valueType: string;
                if (Number.isInteger(item.value)) {
                    valueType = 'INTEGER';
                } else {
                    valueType = 'DOUBLE';
                }
                return {
                    fieldId: item.fieldId,
                    value: item.value,
                    fieldCode: item.fieldCode,
                    valueType,
                };
            case FieldType.IMAGE:
            case FieldType.VIDEO:
            case FieldType.IMAGE_VIDEO:
            case FieldType.SIGNATURE:
                return {
                    fieldId: item.fieldId,
                    fieldCode: item.fieldCode,
                    fileKey: item.fileKey,
                    fileName: item.fileName,
                    fileFormat: item.fileFormat,
                    fileSize: item.fileSize,
                    url: item.url,
                    assetFormId: this.dataSource.assetFormId,
                };
            case FieldType.SINGLE_SELECT:
            case FieldType.MULTI_SELECT:
                return {
                    fieldId: item.fieldId,
                    fieldCode: item.fieldCode,
                    optionId: item.optionId,
                    optionType: item.optionType,
                    assetFormId: this.dataSource.assetFormId,
                };
            case FieldType.ISSUES:
                return {
                    fieldId: item.fieldId,
                    fieldCode: item.fieldCode,
                    optionId: item.optionId,
                    isIssue: item.isIssue,
                    isResolved: item.isResolved,
                    comment: item.comment,
                    mediaList: item.mediaList,
                };
            case FieldType.TABLE_NUMBER_COLUMNS:
                return {
                    fieldId: item.fieldId,
                    fieldCode: item.fieldCode,
                    columnId: item.columnId,
                    columnLabel: item.columnLabel,
                    columnOrder: item.columnOrder,
                    rowIndex: item.rowIndex,
                    value: item.value,
                    valueType: item.valueType,
                };
            case FieldType.TABLE_TEXT_COLUMNS:
                return {
                    fieldId: item.fieldId,
                    fieldCode: item.fieldCode,
                    columnId: item.columnId,
                    columnLabel: item.columnLabel,
                    columnOrder: item.columnOrder,
                    rowIndex: item.rowIndex,
                    value: item.value,
                };
        }
    }

    public previous(): void {
        this.pageMode.subscribe((pageMode) => {
            this.buttonAction = ButtonActionType.Prev;
            this.continueEvent = this.continueEvent - 1;
            this.cd.detectChanges();
        });
        this.scrollToTop.emit();
    }

    public next(): void {
        this.pageMode.subscribe((pageMode) => {
            switch (pageMode) {
                case FormMode.EDIT:
                    this.formCanvasComponent.checkValidationEvent(true);
                    this.resultEmitEvent();
                    this.buttonAction = ButtonActionType.Next;
                    this.continueEvent = this.continueEvent + 1;
                    this.formCanvasComponent.checkValidationEvent(false);
                    this.cd.detectChanges();
                    break;
                default:
                    this.buttonAction = ButtonActionType.Next;
                    this.continueEvent = this.continueEvent + 1;
                    this.cd.detectChanges();
                    break;
            }
        });
        this.scrollToTop.emit();
    }

    private ensureArray(value) {
        return Array.isArray(value) ? value : [value];
    }

    private updateSectionRequiredValues() {
        const completedValue = this.currentDisplayFields.filter((control) => control.valid);
        const invalidValue = this.currentDisplayOptionalFields ? this.currentDisplayOptionalFields.length : 0;
        const totalField =
            this.currentDisplayTableFields?.totalRequiredValues?.length > 0
                ? this.currentDisplayFields.length + this.currentDisplayTableFields.totalRequiredValues.length
                : this.currentDisplayFields.length;
        const completedField =
            this.currentDisplayTableFields?.totalRequiredAndValid?.length > 0
                ? completedValue.length + this.currentDisplayTableFields.totalRequiredAndValid.length
                : completedValue.length;
        this.sectionRequiredStatus.setValue({
            total: totalField,
            completed: completedField,
            invalid: invalidValue,
        });
        this.cd.detectChanges();
    }

    private updateTotalRequiredValues() {
        timer(100).subscribe(() => {
            if (this.currentFormResults) {
                const totalRequiredValues = [];
                const totalRequiredAndValid = [];
                // Also track any invalid fields, used for section red dot notifications
                const totalInvalidFields = [];
                const invalidFields = [];
                const tableValidation: TableValidation = this.getTableValidation(
                    Object.keys(this.fieldResultForm.controls),
                );
                totalRequiredValues.push(...tableValidation.totalRequiredValues);
                totalRequiredAndValid.push(...tableValidation.totalRequiredAndValid);
                totalInvalidFields.push(...tableValidation.totalInvalidFields);
                Object.keys(this.fieldResultForm.controls).forEach((fieldId: string, index: number) => {
                    const control: AbstractControl = this.fieldResultForm.get(fieldId);
                    if (control.validator) {
                        const validatorFn: ValidationErrors = control.validator({} as AbstractControl);
                        if (validatorFn && validatorFn.required) {
                            totalRequiredValues.push({control, id: fieldId});
                            if (control.valid) {
                                totalRequiredAndValid.push({control, id: fieldId});
                            }
                        }
                        if (control.invalid) {
                            totalInvalidFields.push(parseInt(fieldId));
                        }
                    }
                    // For the custom error e.g. toManyMedia
                    if (control.invalid && control.errors) {
                        if (control.errors['tooManyMedia']) {
                            invalidFields.push(parseInt(fieldId));
                        }
                    }
                });
                const valueArr = [];
                Object.keys(this.currentFormResults).forEach((key: string) => {
                    if (typeof this.currentFormResults[key] === 'object') {
                        this.currentFormResults[key].forEach((item) => {
                            valueArr.push(item.fieldId);
                        });
                    }
                });

                const totalCompleteAndValid: number = totalRequiredAndValid.length;

                this.totalInvalidFieldsList.next(totalInvalidFields);
                this.totalRequiredStatus.setValue({
                    total: totalRequiredValues.length,
                    completed: totalCompleteAndValid,
                    invalid: invalidFields.length,
                });
                this.cd.detectChanges();
            }
        });
    }

    /** Returns true if a field is SINGLE/MULTI SELECT, or ISSUE type */
    fieldIsSelectType(controlField: FormField): boolean {
        return [FieldType.SINGLE_SELECT, FieldType.MULTI_SELECT, FieldType.ISSUES].includes(controlField?.fieldType);
    }

    /** Returns true field has no configured options */
    checkIfFieldHasNoOptions(controlField: FormField): boolean {
        return controlField.options?.length == 0;
    }

    ngAfterViewInit(): void {
        this.updateTotalRequiredValues();
        this.updateSectionRequiredValues();
    }

    resultEmitEvent() {
        this.formValueChangeEvent.emit(this.currentFormResults);
        this.totalRequiredStatusEmitEvent();
    }

    totalRequiredStatusEmitEvent() {
        this.completedRequiredFields.emit(this.totalRequiredStatus.value);
    }

    /** Validation for table */
    private getMergedColumns(): any[] {
        return [...this.currentFormResults.tableNumberColumns, ...this.currentFormResults.tableTextColumns];
    }

    private getTableValidation(fieldForm): TableValidation {
        const mergedValidation: TableValidation = {
            totalRequiredValues: [],
            totalRequiredAndValid: [],
            totalInvalidFields: [],
        };
        fieldForm.forEach((fieldId: string) => {
            const control: AbstractControl = this.fieldResultForm.get(fieldId);
            if (control?.value?.value?.fieldType === FieldType.TABLE) {
                const validatorFn: ValidationErrors = control.validator?.({} as AbstractControl);

                if (validatorFn?.required) {
                    const mergedColumns = this.getMergedColumns();
                    const columnList = this.getColumnList(fieldId);

                    // Get validation result and merge it into `mergedValidation`
                    const validationResult = this.processColumnValidation(control, fieldId, columnList, mergedColumns);
                    mergedValidation.totalRequiredValues.push(...validationResult.totalRequiredValues);
                    mergedValidation.totalRequiredAndValid.push(...validationResult.totalRequiredAndValid);
                    mergedValidation.totalInvalidFields.push(...validationResult.totalInvalidFields);
                }
            }
        });
        return mergedValidation;
    }

    private getColumnList(fieldId: string): any[] {
        const tableField = this.allFieldList.find((form) => form.id === Number(fieldId))?.tableRows || [];

        let columnList = [];
        tableField.forEach((row) => {
            row.columns.forEach((column) => {
                columnList.push({
                    columnId: Number(column.id),
                    columnOrder: Number(column.order),
                    fieldId: Number(row.formFieldId),
                    rowIndex: row.rowIndex,
                    formFieldId: row.formFieldId,
                });
            });
        });

        return columnList;
    }

    private processColumnValidation(
        control: AbstractControl,
        fieldId: string,
        columnList: any[],
        mergedColumns: any[],
    ) {
        const totalRequiredValues = [];
        const totalRequiredAndValid = [];
        const totalInvalidFields = [];
        columnList.forEach((item) => {
            let isMatched = false;
            totalRequiredValues.push({control, id: `${fieldId}-${item.rowIndex}-${item.columnOrder}`});

            mergedColumns.forEach((result) => {
                if (
                    result.fieldId === item.formFieldId &&
                    result.rowIndex === item.rowIndex &&
                    result.columnId === item.columnId &&
                    result.columnOrder === item.columnOrder
                ) {
                    isMatched = true;
                    const typeValidation = this.validateType(result.value, result.valueType || null);
                    if (!typeValidation) {
                        totalInvalidFields.push(`${fieldId}-${item.rowIndex}-${item.columnOrder}`);
                    } else {
                        totalRequiredAndValid.push({
                            control,
                            id: `${fieldId}-${item.rowIndex}-${item.columnOrder}`,
                        });
                    }
                }
            });

            if (!isMatched) {
                totalInvalidFields.push(`${fieldId}-${item.rowIndex}-${item.columnOrder}`);
            }
        });
        return {
            totalRequiredValues,
            totalRequiredAndValid,
            totalInvalidFields,
        };
    }

    validateType(value, type?) {
        if (!type) {
            return typeof value === 'string';
        }
        if (type) {
            switch (type.toLowerCase()) {
                case 'integer':
                    return Number.isInteger(value);
                case 'double':
                    return typeof value === 'number' && !Number.isNaN(value) && !Number.isInteger(value);
                default:
                    break;
            }
        }
    }

    findSectionById(sections: FormSection[], id: number): FormSection | undefined {
        for (const section of sections) {
            if (section.id === id) {
                return section;
            }
            if (section.sections) {
                const found = this.findSectionById(section.sections, id);

                if (found) {
                    return section;
                }
            }
        }
        return undefined;
    }
}
